import React, { useEffect, useState } from 'react';
import { Step, Steps, useSteps } from 'chakra-ui-steps';
import { Box, Divider, Grid, Image, Flex, useClipboard } from '@chakra-ui/react';
import { ChevronRightIcon, CopyIcon } from '@chakra-ui/icons';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import Text from './Text';
import { isEmpty, notEmpty, notNull, reverseArray, shortenAddress } from '../utils/core';
import TokenImage from './TokenImage';
import {
  calculateBridgeFee,
  /* calculateGasPriceRange, */
  getSimpleSteps,
} from '../lib/workflow';
import Linkable from './Linkable';

const DetailItem = ({ icon, label = '', value = '', footer }) => {
  const Img = icon;
  return (
    <Box w="100%" h="50">
      <Flex>
        {icon ? <Img /> : null}
        <Box d="block" ml="15">
          <Text textStyle="small" as="h3">{label}</Text>
          <Text textStyle="body">{value}</Text>
          {footer && <Text textStyle="small" as="h5">{footer}</Text>}
        </Box>
      </Flex>
    </Box>
  );
};

const stepStateMap = {
  error: {
    text: 'Failed',
    color: 'valencia',
  },
  retrying: {
    text: 'Error, retrying ...',
    color: 'valencia',
  },
  loading: {
    text: 'Processing ...',
    color: 'richBlack',
  },
  completed: {
    text: 'Completed',
    color: 'richBlack',
  },
};

const TransferOverview = ({ transfer = {}, link, ...props }) => {
  const {
    destination = {},
    origin = {},
    logs = [],
    token = {},
    token: {
      address = 'Unknown',
      ercType,
      ercTypeLabel,
      units: value,
      tokenId = 'Unknown',
      symbol,
      name,
      label = 'Unknown',
    } = {},
    events = [],
    bridgeFees = {},
    started,
    updated = started,
    completed,
    duration,
  } = transfer;

  const {
    token: {
      address: destinationContractAddress,
    } = {},
  } = events.find(({ eventName } = {}) => eventName === 'executeProposal') || {};

  const reversedLogs = reverseArray(logs);
  const getSteps = () => getSimpleSteps({ ...transfer, logs: reversedLogs });
  const [steps, setSteps] = useState(getSteps());
  const {
    greenFee,
    relayFee,
    totalFee = (notNull(greenFee) || notNull(relayFee)) ? calculateBridgeFee(greenFee, relayFee) : undefined,
    // @TODO - what are the modes meant to be? E.g. Fast, Standard etc
    mode = 'Standard',
    // estimate = 'Unknown time estimate'
  } = bridgeFees;
  const { setStep: setLatestStepIndex } = useSteps({ initialStep: 0 });
  const [activeStepStatus, setActiveStepStatus] = useState('loading');
  useEffect(() => {
    if (notEmpty(logs)) {
      setSteps(getSteps());
      // @TODO - include 'error' state
      const { latestStepIndex, latestStep: { completed, inProgress } = {} } = steps;
      setLatestStepIndex(latestStepIndex);
      setActiveStepStatus(!completed || inProgress ? 'loading' : 'completed');
    }
  }, [JSON.stringify(logs)]);
  const tokenValue = `${value ? `${parseFloat(value).toString()} ` : ''}`;
  // const { minPrice, maxPrice } = calculateGasPriceRange(events);
  const Container = Linkable(link);

  // Copy contract address buttons
  const [copyValue, setCopyValue] = useState();
  const { onCopy } = useClipboard(copyValue);
  useEffect(() => {
    if (copyValue) {
      onCopy();
    }
  }, [copyValue]);

  return (isEmpty(transfer) ? null : (
    <Container bgColor="white" border="1px solid" borderColor="richBlack" {...props}>
      <Box padding="24px">
        <Flex alignItems="center" mb="24px">
          <TokenImage boxSize="32px" mr="15px" token={token} expandable />
          <Box>
            {ercType === 1155 ? (
              <Text textStyle="h4" as="h4" d="flex" alignItems="baseline">
                {`${value > 0 ? ` (${value})` : ''} ${symbol || name} [${tokenId}]`}
              </Text>
            ) : (
              <Text textStyle="h4" as="h4" d="flex" alignItems="baseline">
                {`${tokenValue}${symbol || name || label}`}
                {ercTypeLabel && <Text textStyle="body" ml="4px">{`(${ercTypeLabel})`}</Text>}
              </Text>
            )}
            <Flex>
              <Flex
                justifyContent="center"
                alignItems="center"
                title={`${address} (${origin.name})`}
              >
                <Text
                  textStyle="small"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginTop: 1,
                  }}
                >
                  {`${shortenAddress(address)} (${origin.name})`}
                </Text>
                <CopyIcon
                  style={{ height: '10px', marginLeft: '2px', marginBottom: '-3px' }}
                  onClick={() => { setCopyValue(address); }}
                  _hover={{ cursor: 'pointer' }}
                />
              </Flex>
              {!destinationContractAddress ? null : (
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  title={`${destinationContractAddress} (${destination.name})`}
                  ml="7px"
                >
                  <Text
                    textStyle="small"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginTop: 1,
                    }}
                  >
                    {`${shortenAddress(destinationContractAddress)} (${destination.name})`}
                  </Text>
                  <CopyIcon
                    style={{ height: '10px', marginLeft: '2px', marginBottom: '-3px' }}
                    onClick={() => { setCopyValue(destinationContractAddress); }}
                    _hover={{ cursor: 'pointer' }}
                  />
                </Flex>
              )}
            </Flex>
          </Box>
          {link ? <ChevronRightIcon fontSize="32px" pb="2px" /> : null}
        </Flex>
        <Grid templateColumns="repeat(2, 1fr)" columnGap="24px" rowGap="24px">
          <DetailItem
            icon={() => (
              <Box d="flex" flexDirection="column" justifyContent="center">
                <Box boxSize="32px"><Image src={`/${origin.img}`} /></Box>
                <Image mt={['8px', '-2px']} objectFit="scale-down" src="vector.svg" />
              </Box>
            )}
            label="Origin network"
            value={origin.label}
          />
          <DetailItem
            label="Bridge fee"
            value={notNull(totalFee) ? `${mode} (${totalFee} DAI)` : 'No fee'}
          // @TODO - provide a bridge fee estimate
          // footer={estimate}
          />
          <DetailItem
            icon={() => (
              <Box d="flex" flexDirection="column" justifyContent="center">
                <Box boxSize="32px"><Image src={`/${destination.img}`} /></Box>
              </Box>
            )}
            label="Destination network"
            value={destination.label}
          />
          {/* <DetailItem
            label="Gas price"
            value={`${minPrice} - ${maxPrice} Gwei`}
          /> */}
        </Grid>
      </Box>
      <Box paddingLeft="2px" paddingRight="2px">
        <Divider orientation="horizontal" borderColor="grey5" />
      </Box>
      <Box padding="24px">
        <Steps
          responsive={false}
          state={activeStepStatus}
          size="sm"
          activeStep={steps.activeStepIndex}
          colorScheme={!completed ? 'projectGrey' : 'successGreenAll'}
          mb="24px"
          style={{ justifyContent: 'flex-start' }}
        >
          {completed
            ? [<Step isCompletedStep key="1" />]
            : steps.steps.map(({ key = uuidv4(), completed }) => (
              <Step
                isCompletedStep={completed}
                key={key}
              />
            ))}
        </Steps>
        <Text
          as="h3"
          textStyle="h3"
          style={{ color: (stepStateMap[activeStepStatus] || {}).color }}
          mb="10px"
        >
          {(steps.latestStep || {}).label}
        </Text>
        <Flex flexWrap="wrap">
          {!['error', 'completed'].includes(activeStepStatus) ? null : (
            <Text mr="5px" as="h3" textStyle="bodyGrey2">
              {completed ? 'Completed: ' : 'Started:'}
            </Text>
          )}
          <Text textStyle="bodyGrey2" style={{ fontWeight: 'bold' }} mr="3px">
            {`${moment(completed ? updated : started).format('LLL')}`}
          </Text>
          <Text textStyle="bodyGrey2" style={{ fontWeight: 'bold' }}>
            {`  (${moment(completed || updated).subtract(duration, 'milliseconds').fromNow()})`}
          </Text>
        </Flex>
        {/* {['completed', 'error'].includes(activeStepStatus) ? null : (
          <Flex>
            <Text mr="5px" as="h3" textStyle="bodyGrey2">
              Estimated time to complete:
            </Text>
            <Text textStyle="bodyGrey2" style={{ fontWeight: 'bold' }}>
              {dateDiff(completedAtEstimate, new Date())}
            </Text>
          </Flex>
        )} */}
      </Box>
    </Container>
  ));
};

export default TransferOverview;
